import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, Download } from 'lucide-react';
import { toast } from 'sonner';

// Shared Client Report download dialog — opens from the Clients list card,
// the Client Profile page, and anywhere else a client report is downloaded.
// The actual report content and privacy rules are enforced server-side; this
// dialog is purely a date-range + format picker with a live item preview.

const PRESETS = [
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'last_3_months', label: 'Last 3 months' },
  { key: 'this_year', label: 'This year' },
  { key: 'custom', label: 'Custom' },
];

const iso = (d) => d.toISOString().slice(0, 10);

function rangeForPreset(preset) {
  const today = new Date();
  const y = today.getFullYear();
  const m = today.getMonth();
  if (preset === 'this_month') {
    return { from: iso(new Date(y, m, 1)), to: iso(today) };
  }
  if (preset === 'last_month') {
    const first = new Date(y, m - 1, 1);
    const last = new Date(y, m, 0);
    return { from: iso(first), to: iso(last) };
  }
  if (preset === 'last_3_months') {
    const first = new Date(y, m - 2, 1);
    return { from: iso(first), to: iso(today) };
  }
  if (preset === 'this_year') {
    return { from: iso(new Date(y, 0, 1)), to: iso(today) };
  }
  // custom: no change
  return null;
}

function niceDate(iso10) {
  if (!iso10) return '';
  const [y, m, d] = iso10.split('-').map(Number);
  if (!y) return '';
  return new Date(y, m - 1, d).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

export default function ClientReportDialog({ open, onOpenChange, client, defaultFormat = 'pdf' }) {
  const [preset, setPreset] = useState('last_month');
  const [range, setRange] = useState(() => rangeForPreset('last_month'));
  const [format, setFormat] = useState(defaultFormat);
  const [layout, setLayout] = useState('multi'); // 'multi' (full) | 'single' (one-pager)
  const [preview, setPreview] = useState(null); // {items, from, to, posts, reels}
  const [previewing, setPreviewing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [previewOpening, setPreviewOpening] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setPreset('last_month');
    setRange(rangeForPreset('last_month'));
    setFormat(defaultFormat);
    setLayout('multi');
    setError('');
    setPreview(null);
  }, [open, defaultFormat, client?.id]);

  const changePreset = (val) => {
    setPreset(val);
    const r = rangeForPreset(val);
    if (r) setRange(r);
  };

  const validateRange = (r) => {
    if (!r?.from || !r?.to) return 'Pick a start and end date.';
    if (r.from > r.to) return "'From' must be on or before 'To'.";
    const today = iso(new Date());
    if (r.to > today) return "'To' can't be in the future.";
    // ≤ 12 months
    const a = new Date(r.from), b = new Date(r.to);
    const diffMo = (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth());
    if (diffMo > 12) return 'Pick a range of 12 months or less.';
    return '';
  };

  // Live preview — count only, no hours/names.
  useEffect(() => {
    if (!open || !client?.id || !range?.from || !range?.to) return;
    const err = validateRange(range);
    setError(err);
    if (err) { setPreview(null); return; }
    let cancelled = false;
    setPreviewing(true);
    (async () => {
      try {
        const res = await api.get(`/clients/${client.id}/report-preview`, {
          params: { from: range.from, to: range.to },
        });
        if (!cancelled) setPreview(res.data);
      } catch (e) {
        if (!cancelled) {
          setPreview(null);
          setError(e?.response?.data?.detail || 'Could not fetch preview');
        }
      } finally {
        if (!cancelled) setPreviewing(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, client?.id, range?.from, range?.to]);

  const canDownload = useMemo(
    () => !error && !!preview && (preview.items || 0) > 0 && !downloading,
    [error, preview, downloading],
  );

  const download = async () => {
    if (!client?.id || !range?.from || !range?.to) return;
    setDownloading(true);
    try {
      const res = await api.get('/reports/client-work', {
        params: {
          client_id: client.id,
          date_from: range.from,
          date_to: range.to,
          format,
          layout,
        },
        responseType: 'blob',
      });
      // Server can return a JSON empty-state when there are no items.
      const ct = res.headers?.['content-type'] || '';
      if (ct.includes('application/json')) {
        const text = await res.data.text();
        try {
          const j = JSON.parse(text);
          toast.message(j.message || 'No work found in this period.');
        } catch { toast.message('No work found in this period.'); }
        return;
      }
      const safe = (client.company || client.name || 'client').replace(/[^A-Za-z0-9_\-]/g, '_');
      const ext = format === 'xlsx' ? 'xlsx' : (format === 'csv' ? 'csv' : 'pdf');
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `shine_client_report_${safe}_${range.from}_to_${range.to}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Client Report downloaded');
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  // Preview = open the generated PDF inline in a new tab (same params as the
  // real download). Browser decides whether to render it or save it.
  const openPreview = async () => {
    if (!client?.id || !range?.from || !range?.to) return;
    setPreviewOpening(true);
    try {
      const res = await api.get('/reports/client-work', {
        params: {
          client_id: client.id,
          date_from: range.from,
          date_to: range.to,
          format: 'pdf',
          layout,
        },
        responseType: 'blob',
      });
      const ct = res.headers?.['content-type'] || '';
      if (ct.includes('application/json')) {
        const text = await res.data.text();
        try { toast.message(JSON.parse(text).message || 'Nothing to preview'); }
        catch { toast.message('Nothing to preview'); }
        return;
      }
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank', 'noopener,noreferrer');
      // Delay revoke so the new tab has time to load it.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
    } finally {
      setPreviewOpening(false);
    }
  };

  // Range preset chips (S21-style)
  const CHIPS = [
    { key: 'this_month', label: 'This month' },
    { key: 'last_month', label: 'Last month' },
    { key: 'last_3_months', label: 'Last 3 months' },
    { key: 'custom', label: 'Custom' },
  ];
  const noWork = !!(preview && !previewing && (preview.items || 0) === 0 && !error);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="client-report-dialog">
        <DialogHeader>
          <DialogTitle>Client report</DialogTitle>
        </DialogHeader>

        {/* Safe-to-share banner */}
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900"
             data-testid="report-safe-banner">
          <b>Safe to share</b> — shows work delivered, never hours or team names.
        </div>

        <div className="space-y-4">
          <div>
            <Label className="text-xs text-slate-500">Client</Label>
            <div className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800">
              {client?.company || client?.name || '—'}
            </div>
          </div>

          <div>
            <Label>Date range</Label>
            <div className="flex gap-1 flex-wrap mt-1">
              {CHIPS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  data-selectable
                  data-selected={preset === p.key ? 'true' : 'false'}
                  onClick={() => changePreset(p.key)}
                  className={`shine-pill-dark px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                    preset === p.key ? '' : 'bg-white text-slate-600 border-slate-200'
                  }`}
                  data-testid={`report-preset-${p.key}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>From</Label>
              <Input
                type="date"
                value={range?.from || ''}
                max={range?.to || undefined}
                onChange={(e) => { setPreset('custom'); setRange({ ...(range || {}), from: e.target.value }); }}
                data-testid="report-date-from"
              />
            </div>
            <div>
              <Label>To</Label>
              <Input
                type="date"
                value={range?.to || ''}
                min={range?.from || undefined}
                max={iso(new Date())}
                onChange={(e) => { setPreset('custom'); setRange({ ...(range || {}), to: e.target.value }); }}
                data-testid="report-date-to"
              />
            </div>
          </div>

          <div>
            <Label>Format</Label>
            <Select value={format} onValueChange={setFormat}>
              <SelectTrigger data-testid="report-format"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pdf">PDF</SelectItem>
                <SelectItem value="xlsx">Excel</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Layout toggle: Full report (multi-page) vs One-page summary */}
          {format === 'pdf' && (
            <div>
              <Label>Pages</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <button
                  type="button"
                  data-selectable
                  data-selected={layout === 'multi' ? 'true' : 'false'}
                  onClick={() => setLayout('multi')}
                  className="shine-selectable-mark rounded-md border px-3 py-2 text-left text-xs border-slate-200 bg-white text-slate-600 transition"
                  data-testid="report-layout-multi"
                >
                  <div className="font-semibold text-sm">Full report</div>
                  <div className="text-[11px] opacity-80">Summary + every deliverable listed</div>
                </button>
                <button
                  type="button"
                  data-selectable
                  data-selected={layout === 'single' ? 'true' : 'false'}
                  onClick={() => setLayout('single')}
                  className="shine-selectable-mark rounded-md border px-3 py-2 text-left text-xs border-slate-200 bg-white text-slate-600 transition"
                  data-testid="report-layout-single"
                >
                  <div className="font-semibold text-sm">One-page summary</div>
                  <div className="text-[11px] opacity-80">Just the KPIs + format split</div>
                </button>
              </div>
            </div>
          )}

          {/* Live preview line */}
          {!noWork && (
            <div className="flex items-center justify-between gap-2 text-xs min-h-[1.25rem]"
                 data-testid="report-preview-line">
              <div className="text-slate-500">
                {error ? (
                  <span className="text-red-600 font-medium">{error}</span>
                ) : previewing ? (
                  <span className="inline-flex items-center gap-1">
                    <Loader2 className="h-3 w-3 animate-spin" /> Counting items…
                  </span>
                ) : preview ? (
                  <span className="inline-flex items-center gap-2">
                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" />
                    <b className="text-slate-800">{preview.items} items</b> found
                    {(preview.posts || preview.reels) ? (
                      <span className="text-slate-500">
                        · {preview.posts || 0} posts, {preview.reels || 0} reels
                      </span>
                    ) : null}
                  </span>
                ) : (
                  <>{niceDate(range?.from)} – {niceDate(range?.to)}</>
                )}
              </div>
              {preview && (preview.items || 0) > 0 && !error && (
                <button
                  type="button"
                  onClick={openPreview}
                  disabled={previewOpening}
                  className="text-emerald-700 hover:underline inline-flex items-center gap-1"
                  data-testid="report-preview-link"
                >
                  {previewOpening ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                  Preview
                </button>
              )}
            </div>
          )}

          {/* Empty-state card (S21 B) */}
          {noWork && (
            <div className="rounded-md border-2 border-dashed border-slate-200 bg-slate-50/60 p-5 text-center"
                 data-testid="report-empty-state">
              <div className="mx-auto h-10 w-10 rounded-full bg-white border border-slate-200 flex items-center justify-center">
                <span className="text-xl">📋</span>
              </div>
              <div className="mt-2 font-semibold text-slate-800">No work found</div>
              <div className="text-xs text-slate-500 mt-1">
                Nothing was logged for <b>{client?.company || client?.name}</b> between{' '}
                <b>{niceDate(range?.from)}</b> and <b>{niceDate(range?.to)}</b>, so there's no report to download.
              </div>
              <div className="mt-3 flex flex-wrap gap-1 justify-center">
                <button
                  type="button"
                  onClick={() => {
                    const d = new Date(); const from = new Date(); from.setDate(from.getDate() - 30);
                    setPreset('custom');
                    setRange({ from: iso(from), to: iso(d) });
                  }}
                  className="px-3 py-1.5 rounded-full text-xs bg-white border border-slate-200 hover:border-slate-400 text-slate-700"
                  data-testid="report-try-30"
                >
                  Try last 30 days
                </button>
                <button
                  type="button"
                  onClick={() => changePreset('last_month')}
                  className="px-3 py-1.5 rounded-full text-xs bg-white border border-slate-200 hover:border-slate-400 text-slate-700"
                  data-testid="report-try-last-month"
                >
                  Try last month
                </button>
              </div>
              <div className="text-[11px] text-slate-400 mt-3">
                Expecting work here? Check that the team logged it in Daily Reports against this client.
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="report-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={download}
            disabled={!canDownload}
            data-testid="report-download-btn"
            className="bg-emerald-600 hover:bg-emerald-500 text-white"
          >
            {downloading
              ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Generating…</>
              : <><Download className="h-4 w-4 mr-1" /> Download {format === 'xlsx' ? 'Excel' : 'PDF'}</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
